import { Injectable } from '@angular/core';

const viLoggerVersion = '1.0.0';

export enum LogType {
  any = 'any',
  http = 'http',
  httpResponsePayload = 'http_response_payload',
  functionExecutionTime = 'function_execution_time',
  info = 'info',
  websocket = 'websocket',
}

@Injectable({ providedIn: 'root' })
export class ViLogger {
  // TODO: Write unit tests for ViLogger
  // TODO: Add Class decorator - to log constructor execution time

  private style = 'color:gray;';
  private isEnabled: Boolean = false;
  private selectedLogTypes: LogType[] = [
    LogType.any,
    LogType.functionExecutionTime,
    LogType.http,
    LogType.httpResponsePayload,
    LogType.info,
    LogType.websocket,
  ];
  private readonly turnOnLoggerKeyword = 'uilog=on';
  private readonly logTypeParamName = 'uilogfilterby';
  private readonly logTypeSeparatorChar = ',';

  constructor() {
    this.isEnabled =
      window.performance &&
      window.performance.mark &&
      window.location.href.indexOf(this.turnOnLoggerKeyword) !== -1;
    const selectedLogTypes = this.getLogTypesFromQueryString();
    if (selectedLogTypes) {
      this.selectedLogTypes = selectedLogTypes;
    }
  }

  // PUBLIC API
  // log
  public log(content: any, logType: LogType): void {
    if (!this.isEnabled || !this.isLogTypeSelected(logType)) {
      return;
    }

    console.log(content);
  }

  // mark
  public mark(mark: string, logType: LogType = LogType.any): void {
    if (!this.isEnabled || !this.isLogTypeSelected(logType)) {
      return;
    }

    performance.mark(mark);
  }

  // start
  public start(mark: string, logType: LogType = LogType.any, content: any = ''): void {
    if (!this.isEnabled || !this.isLogTypeSelected(logType)) {
      return;
    }

    performance.mark(mark);
    console.log(`%c${this.info()}   ${mark} ${content}`, this.style);
  }

  // end
  public end(mark: string, logType: LogType = LogType.any, content: any = ''): void {
    if (!this.isEnabled || !this.isLogTypeSelected(logType)) {
      return;
    }

    const measure = mark + '-measure';
    const endMark = 'logger-end-mark';

    performance.mark(endMark);
    try {
      performance.measure(measure, mark, endMark);
    } catch (e) {
      console.log(
        `%c--- Failed to log performance. The mark: '${mark}' does not exist.`,
        'color:red;'
      );

      return;
    }

    const currentMarkMeasure = performance.getEntriesByName(measure)[0];

    console.log(
      `%c${this.info()} | ${this.toSeconds(currentMarkMeasure.duration)} ${mark} ${content}`,
      this.style
    );
  }

  private toSeconds(time: number): string {
    const s = time / 1000;

    return `${s.toFixed(4)}s`;
  }

  private info(): string {
    const mark = performance.getEntriesByName('indexInit')[0];

    if (mark === undefined) {
      // this check is for unit tests - indexInit mark does not exist when unit tests are run

      return;
    }

    const now = performance.now();

    return `--- ${time()}  ${this.toSeconds(now - mark.startTime)}`;

    function time(): string {
      const d = new Date();

      return `${d.getHours()}:${d.getMinutes()}:${d.getSeconds()}:${d.getMilliseconds()}`;
    }
  }

  private getLogTypesFromQueryString(): LogType[] {
    const types = this.getUrlParameter(this.logTypeParamName, location.search);
    if (!types) {
      return null;
    }

    const logTypes = this.matchLogTypes(types.split(this.logTypeSeparatorChar));

    return logTypes;
  }

  private getUrlParameter(name: string, search: string): string {
    // https://davidwalsh.name/query-string-javascript
    name = name.replace(/[\[]/, '\\[').replace(/[\]]/, '\\]');
    const regex = new RegExp('[\\?&]' + name + '=([^&#]*)');
    const results = regex.exec(search);
    const types = results === null ? '' : decodeURIComponent(results[1].replace(/\+/g, ' '));

    return types;
  }

  private matchLogTypes(parsedParams: string[]): LogType[] {
    const types = [];

    parsedParams.forEach((typeParam) => {
      switch (typeParam) {
        case LogType.any:
          types.push(LogType.any);
          break;
        case LogType.http:
          types.push(LogType.http);
          break;
        case LogType.httpResponsePayload:
          types.push(LogType.httpResponsePayload);
          break;
        case LogType.functionExecutionTime:
          types.push(LogType.functionExecutionTime);
          break;
        case LogType.info:
          types.push(LogType.info);
          break;
        case LogType.websocket:
          types.push(LogType.websocket);
          break;
        default:
          break;
      }
    });

    return types.length ? types : null;
  }

  private isLogTypeSelected(logType: LogType): boolean {
    if (logType === LogType.any) {
      return true;
    }

    return this.selectedLogTypes.indexOf(logType) !== -1;
  }

  private logInfoAboutLogger(): void {
    this.log('****************************', LogType.any);
    this.log('*** UI Logger Turned ON', LogType.any);
    this.log(`*** UI Logger version: ${viLoggerVersion}`, LogType.any);
    this.log('*** query string params used by the UI Logger:', LogType.any);
    this.log('*** uilog=on - enables UI logging', LogType.any);
    this.log(
      '*** uilogfilterby=logType1,logType2, etc... - filters logging by specified log types',
      LogType.any
    );
    this.log('*** Possible Log Types:', LogType.any);
    this.log(
      '*** http - log info about outgoing http requests as well as incoming http responses',
      LogType.any
    );
    this.log('*** http_response_payload - log http response payload', LogType.any);
    this.log('*** info - log custom info such as application business logic info', LogType.any);
    this.log('*** websocket - log websocket responses', LogType.any);
    this.log(
      `*** function_execution_time - log function's execution time (functions decorated by the Log() decorator)`,
      LogType.any
    );

    this.log('*** when uilogfilterby is not provided then everything is logged ', LogType.any);
    this.log('****************************', LogType.any);
  }
}

// logger instance used by the 'Log' decorator
export const logger = new ViLogger();

// log info about logger
if (logger['isEnabled']) {
  logger['logInfoAboutLogger']();
}

// Log decorator - uses its own instance of ViLogger class
export function Log(): MethodDecorator {
  return function (target: Function, methodName: string, descriptor: any): any {
    const originalMethod = descriptor.value;

    descriptor.value = function (...args: any[]): any {
      const mark = `${target.constructor.name}.${methodName}`;
      logger.start(mark, LogType.functionExecutionTime);

      const result = originalMethod.apply(this, args);

      logger.end(mark, LogType.functionExecutionTime);

      return result;
    };

    return descriptor;
  };
}
